import { Injectable, inject } from '@angular/core';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { FirebaseAwareService } from '../shared/firestore/firebase-aware.service';

/** Owner-facing invoice row (commission invoice the platform issued to them). */
export interface OwnerInvoiceListItem {
  id: string;
  invoiceNumber: string;
  issueDate: string;
  issueDateMs: number;
  bookingId: string;
  spaceName?: string;
  concept: string;
  netCents: number;
  vatCents: number;
  vatRatePercent: number;
  irpfCents: number;
  irpfRatePercent: number;
  totalCents: number;
  issuerLegalName: string;
  issuerTaxId: string;
}

@Injectable({ providedIn: 'root' })
export class OwnerInvoiceService extends FirebaseAwareService {
  private readonly functions = inject(Functions);

  async listMyInvoices(): Promise<OwnerInvoiceListItem[]> {
    return this.inCtx(async () => {
      const call = httpsCallable<void, { invoices: OwnerInvoiceListItem[] }>(
        this.functions,
        'listOwnerInvoices',
      );
      const res = await call();
      return res.data.invoices;
    });
  }

  async getInvoiceHtml(invoiceId: string): Promise<{ invoiceNumber: string; html: string }> {
    return this.inCtx(async () => {
      const call = httpsCallable<{ invoiceId: string }, { invoiceNumber: string; html: string }>(
        this.functions,
        'getOwnerInvoiceHtml',
      );
      const res = await call({ invoiceId });
      return res.data;
    });
  }

  /** Opens stored invoice HTML in a new tab for printing / saving as PDF. */
  openPrintWindow(html: string, title: string): void {
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const win = window.open(url, '_blank');
    if (win) {
      win.document.title = title;
      win.onload = () => URL.revokeObjectURL(url);
    }
  }

  /**
   * Builds a CSV (Excel-friendly, `;` separator + UTF-8 BOM) from invoice rows.
   * Columns match what a Spanish accountant expects in the expense ledger.
   */
  buildCsv(rows: OwnerInvoiceListItem[]): string {
    const header = [
      'Numero', 'Fecha', 'Concepto', 'Espacio',
      'Base imponible (€)', 'IVA %', 'Cuota IVA (€)',
      'IRPF %', 'Retencion IRPF (€)', 'Total (€)',
      'Emisor', 'NIF emisor', 'Reserva',
    ];
    const euro = (cents: number) => (cents / 100).toFixed(2).replace('.', ',');
    const esc = (v: string | number) => {
      const s = String(v ?? '');
      return /[";\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = rows.map(r => [
      r.invoiceNumber, r.issueDate, r.concept, r.spaceName ?? '',
      euro(r.netCents), r.vatRatePercent, euro(r.vatCents),
      r.irpfRatePercent, euro(r.irpfCents), euro(r.totalCents),
      r.issuerLegalName, r.issuerTaxId, r.bookingId,
    ].map(esc).join(';'));
    return '﻿' + [header.join(';'), ...lines].join('\r\n');
  }

  /** Triggers a client-side download of the CSV. */
  downloadCsv(rows: OwnerInvoiceListItem[], filename: string): void {
    const blob = new Blob([this.buildCsv(rows)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }
}
